import { fetchAuthSession } from 'aws-amplify/auth';

const defaultMethod = 'GET';

async function getAuthToken() {
  /** @type {string | null} */
  let authToken = null;
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      authToken = `Bearer ${tokens.idToken.toString()}`;
    }
  }
  catch {
    // user not login, no problem here, just call api without token
  }
  return authToken;
}

/**
 * Build options
 * @param {import('axios').Method} [method]
 * @param {any} [body]
 * @param {any} [customHeaders]
 * @param {{ skipAuthen?: boolean }} [options]
 * @returns {Promise<import('axios').AxiosRequestConfig>}
 */
export async function buildOptions(method, body, customHeaders, options = {}) {
  return {
    method: method || defaultMethod,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': !options.skipAuthen
        ? await getAuthToken()
        : null,
      ...customHeaders
    },
    data: body,
    params: body && body.params ? body.params : null,
    signal: options.signal || null
  };
}

/**
 * @param {import('axios').Method} [method]
 * @param {any} [body]
 * @param {any} [customHeaders]
 * @param {{ skipAuthen?: boolean }} [options]
 * @returns {Promise<import('axios').AxiosRequestConfig>}
 */
export async function buildOptionsZip(method, body, customHeaders, options = {}) {
  return {
    method: method || defaultMethod,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/zip',
      'Authorization': !options.skipAuthen
        ? await getAuthToken()
        : null,
      ...customHeaders
    },
    data: body,
    params: body && body.params ? body.params : null
  };
}

/**
 * @param {import('axios').Method} [method]
 * @param {any} [body]
 * @param {any} [customHeaders]
 * @param {{ skipAuthen?: boolean }} [options]
 * @returns {Promise<import('axios').AxiosRequestConfig>}
 */
export async function buildOptionsMedia(method, body, customHeaders, options = {}) {
  return {
    method: method || defaultMethod,
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': !options.skipAuthen
        ? await getAuthToken()
        : null,
      ...customHeaders
    },
    data: body,
    params: body && body.params ? body.params : null
  };
}

/**
 * Build api server
 */
export function buildApiServer () {
  return import.meta.env.VITE_HEARTBEAT_API_SERVER_HOST;
}

export function buildApiSocketServer () {
  return import.meta.env.VITE_HEARTBEAT_API_SERVER_HOST + '/api/public/ws';
}

/**
 * Build endpoints
 */
export function getEndPoints () {
  return {
    embers: {
      legendPaths: buildApiServer() + '/api/ember/paths/legends',
      legendPathByPractices: buildApiServer() + '/api/ember/practices/ids',
      paths: buildApiServer() + '/api/ember/paths',
      practicesByPathId: buildApiServer() + '/api/ember/practices/by_path_id/{pathId}',
      practices: buildApiServer() + '/api/ember/practices',
      practicesById: buildApiServer() + '/api/ember/practices/:id',
      hpaTags: buildApiServer() + '/api/ember/meta-tags?metagroup=HPA'
    },
    general: {
      types: {
        questiontypes: buildApiServer() + '/api/general/types/questiontypes',
        transformatives: buildApiServer() + '/api/general/types/transformatives'
      }
    },
    question: {
      addFromLibrary: buildApiServer() + '/api/survey-questions/add-from-library',
      searchFromLibrary: buildApiServer() + '/api/survey-questions/search?searchString={1}&searchBy={2}',
      main: buildApiServer() + '/api/survey-questions',
      questionsNotInSurvey: buildApiServer() + '/api/survey/{surveyId}/survey-questions',
      detail: buildApiServer() + '/api/survey-questions/{surveyQuestionId}',
      logic: buildApiServer() + '/api/survey-questions/logic',
      deleteByQuestion: buildApiServer() + '/api/survey-questions/delete-logic-by-question',
      checkValidLogic: buildApiServer() + '/api/survey-questions/check-valid-logic',
      reorderQuestions: buildApiServer() + '/api/surveys/{surveyId}/reorder-items',
      questionLibrary: buildApiServer() + '/api/survey-questions-library'
    },
    pageBreak: {
      fetchPageBreak: buildApiServer() + '/api/surveys/{surveyId}/page-breaks',
      addPageBreak: buildApiServer() + '/api/page-breaks',
      updatePageBreak: buildApiServer() + '/api/page-breaks/{id}',
      deletePageBreak: buildApiServer() + '/api/page-breaks/{id}',
      addPageBreakResources: buildApiServer() + '/api/resources/upload/{mediaType}'
    },
    survey: {
      surveyLogics: buildApiServer() + '/api/surveys/{surveyId}/scoring-logic',
      main: buildApiServer() + '/api/surveys',
      copySurvey: buildApiServer() + '/api/surveys/copy-survey',
      cancel: buildApiServer() + '/api/surveys/:id/cancel',
      detail: buildApiServer() + '/api/surveys/:id',
      detailByUuid: buildApiServer() + '/api/public/surveys/by-uuid/:id?',
      surveyForm: buildApiServer() + '/api/surveys/:id/form',
      detailByStep: buildApiServer() + '/api/surveys/:id/steps/:stepId',
      types: buildApiServer() + '/api/surveys/types',
      deployments: buildApiServer() + '/api/surveys/deployments/list',
      map: buildApiServer() + '/api/surveys/map',
      extract: {
        status: buildApiServer() + '/api/surveys/:id/extract/status',
        download: buildApiServer() + '/api/surveys/extract/survey-data.csv',
        downloadInstances: buildApiServer() + '/api/surveys/extract/instances.csv'
      },
      groupQuestion: {
        groupsByTitle: buildApiServer() + '/api/surveys/:id/groups/by-title',
        groups: buildApiServer() + '/api/surveys/groups',
        groupQuestions: buildApiServer() + '/api/surveys/groups/questions'
      },
      responses: {
        submit: buildApiServer() + '/api/public/surveys/response/submit'
      },
      question: {
        detail: buildApiServer() + '/api/surveys/{surveyId}/questions/{questionId}'
      }
    },
    users: {
      apiVersion: buildApiServer() + '/api/public/hello-world'
    },
    oauth2: {
      createUserIfNotExist: buildApiServer() + '/api/public/oauth2/create-user-if-not-exist'
    },
    variables: {
      fetchPreloadVariable: buildApiServer() + '/api/surveys/{surveyId}/preloaded-variables',
      addPreloadVariable: buildApiServer() + '/api/variable/preloaded-variable',
      fetchSourceVariable: buildApiServer() + '/api/variable/preloaded-variable/source',
      fetchFieldVariable: buildApiServer() + '/api/variable/preloaded-variable/source/{sourceId}/fields',
      updatePreloadVariable: buildApiServer() + '/api/variable/preloaded-variable/{id}',
      deletePreloadVariable: buildApiServer() + '/api/variable/preloaded-variable/{id}',
      addCalculateVariable: buildApiServer() + '/api/variable/calculated-variable',
      updateCalculateVariable: buildApiServer() + '/api/variable/calculated-variable/{id}',
      deleteCalculateVariable: buildApiServer() + '/api/variable/calculated-variable/{id}',
      fetchCalculateVariable: buildApiServer() + '/api/surveys/{surveyId}/calculated-variables'
    },
    hpa: {
      categories: buildApiServer() + '/api/surveys/hpa-scores/categories'
    },
    app: {
      apiVersion: buildApiServer() + '/api/app/version',
      trackGraylog: buildApiServer() + '/api/public/app/graylog'
    }
  };
}
